/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import {
    EvaluationStrategy,
    FieldType,
    IFieldDefinition,
    ISettingGroupDefinition,
    ISettingValueEx,
    Levels,
    SettingLevel
} from "@nuance/hierarchical-settings-ui-domain";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { CmdBuilder } from "@nuance/hap-components";

const logger: ILogger = getLogger();

export type CachedSettingValues = Record<SettingLevel, ISettingValueEx[] | undefined>;

/**
 * Convert setting level to fake UID value
 */
export const SettingLevelToUID: Record<SettingLevel, number> = {
    [SettingLevel.Organization]: 101,
    [SettingLevel.Site]: 202,
    [SettingLevel.Group]: 303,
    [SettingLevel.User]: 404,
    [SettingLevel.Product]: 505,
    [SettingLevel.System]: 606
};

/**
 * Convert setting level to a Levels
 */
export const SettingLevelToAccessLevel: Record<SettingLevel, Levels> = {
    [SettingLevel.Group]: Levels.Group,
    [SettingLevel.Organization]: Levels.Org,
    [SettingLevel.Product]: Levels.None,
    [SettingLevel.Site]: Levels.Site,
    [SettingLevel.System]: Levels.System,
    [SettingLevel.User]: Levels.User
};

/**
 * Information for changing the level
 */
export interface ILevelChangeInfo {
    /**
     * Setting definition
     */
    settingDefinition: ISettingGroupDefinition;
    /**
     * New setting level
     */
    settingLevel: SettingLevel;
}

/**
 * PreviewComponent state
 */
export interface IPreviewComponentState {
    /**
     * Current setting level
     */
    currentLevel: SettingLevel;
    /**
     * Current setting values for the current level
     */
    currentSettingValues: ISettingValueEx[];
    /**
     * Cache of all setting level values
     */
    cachedSettingValues: CachedSettingValues;
    /**
     * Current language
     */
    language: string;

    canSave: boolean;
}

/**
 * PreviewComponents commands
 */
export class PreviewComponentCommands {
    constructor() {
        const cb = new CmdBuilder();
        const cmds = {
            currentLevelChanged: cb.bindCmd(this.currentLevelChangedHandler),
            resetCachedSettings: cb.bindCmd(this.resetCachedSettingsHandler),
            currentLanguageChanged: cb.bindCmd(this.currentLanguageChangedHandler),
            saveCurrentSettings: cb.bindCmd(this.saveCurrentSettingsHandler),
            canSaveChanged: cb.bindCmd(this.canSaveChangedHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IPreviewComponentState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /**
     * Generates default setting values based on the setting definition
     * @param settingDefinition Setting definition
     * @param settingLevel Setting level for the default settings
     * @returns Collection of setting values as defined in the setting definition
     */
    static DefaultSettingValues = (
        settingDefinition: ISettingGroupDefinition,
        settingLevel: SettingLevel
    ): ISettingValueEx[] => {
        return settingDefinition.Fields.filter(i =>
            PreviewComponentCommands.IsFieldDefinitionForSettingLevel(i, settingLevel)
        ).map(i => ({
            SettingName: i.SettingName,
            Value:
                i.FieldDiscriminator === FieldType.Boolean
                    ? i.DefaultValue
                        ? "True"
                        : "False"
                    : i.DefaultValue.toString(),
            Locked: false,
            ValueSource: {
                Level: settingLevel,
                UID: 0,
                Name: "(Setting default)",
                LockApplied: false
            }
        }));
    };

    /**
     * Test if the field definition is for the setting level
     * @param fieldDefinition A field definition
     * @param settingLevel Setting level to test
     * @returns True if the setting field is part of the setting level setting values
     */
    private static IsFieldDefinitionForSettingLevel = (
        fieldDefinition: IFieldDefinition,
        settingLevel: SettingLevel
    ): boolean => {
        const accessLevel = SettingLevelToAccessLevel[settingLevel];
        return (fieldDefinition.AccessLevel & accessLevel) === accessLevel;
    };

    /**
     * Generate a fake name for a setting level
     * @param level Setting level
     * @returns A fake name
     */
    static GetFakeName = (level: SettingLevel): string => {
        let result = "";
        switch (level) {
            case SettingLevel.Organization:
                result = "St Luke's Health System";
                break;

            case SettingLevel.Site:
                result = "Anderson County Hospital";
                break;

            case SettingLevel.Group:
                result = "Pathology";
                break;

            case SettingLevel.User:
                result = "Dr Wilkinson";
                break;

            case SettingLevel.Product:
                result = "DMO";
                break;

            case SettingLevel.System:
                result = "System";
                break;
        }

        return result;
    };

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    /**
     * Handle changing to a new setting level
     * @param state Old state
     * @param changeInfo Change information
     * @returns New state
     */
    currentLevelChangedHandler = (state: IPreviewComponentState, changeInfo: ILevelChangeInfo) => {
        logger.logInformation("Changing setting level", {
            old: SettingLevel[state.currentLevel],
            new: SettingLevel[changeInfo.settingLevel]
        });

        const { settingDefinition: definition, settingLevel: level } = changeInfo;

        const mergeLevels = this.calcLevelsToMerge(definition.EvaluationStrategy, level);

        const newSettingValues: ISettingValueEx[] = PreviewComponentCommands.DefaultSettingValues(
            definition,
            level
        );

        mergeLevels.forEach(i => {
            const cachedValues = state.cachedSettingValues[i];
            if (cachedValues !== undefined) {
                this.mergeSettingValues(newSettingValues, cachedValues, i, changeInfo.settingLevel);
            }
        });

        state.currentLevel = level;
        state.currentSettingValues = newSettingValues;

        return state;
    };

    /**
     * reset cached settings
     * @param state Old state
     * @param definition HS definition
     * @returns new state
     */
    resetCachedSettingsHandler = (
        state: IPreviewComponentState,
        definition: ISettingGroupDefinition
    ) => {
        logger.trackEvent("Reset changed setting values");

        const orgSettingValues = PreviewComponentCommands.DefaultSettingValues(
            definition,
            SettingLevel.Organization
        );

        state.cachedSettingValues = {
            [SettingLevel.Organization]: orgSettingValues,
            [SettingLevel.Site]: undefined,
            [SettingLevel.Group]: undefined,
            [SettingLevel.User]: undefined,
            [SettingLevel.Product]: undefined,
            [SettingLevel.System]: undefined
        };
        state.currentLevel = SettingLevel.Organization;
        state.currentSettingValues = orgSettingValues;

        return state;
    };

    /**
     * Handle changing the current language
     * @param state Old state
     * @param newLanguage New language
     * @returns New state
     */
    currentLanguageChangedHandler = (state: IPreviewComponentState, newLanguage: string) => {
        logger.trackEvent("Changing language displayed", { old: state.language, new: newLanguage });
        state.language = newLanguage;

        return state;
    };

    /**
     * canSave changes
     * @param state Old state
     * @param isValid Is HS values valid
     * @returns New state
     */
    canSaveChangedHandler = (state: IPreviewComponentState, isValid: boolean) => {
        state.canSave = isValid;
        return state;
    };

    /**
     * Saves the setting values to the cache of setting values
     * @param state Old state
     * @param changedSettingValues Changed setting values
     * @returns New state
     */
    saveCurrentSettingsHandler = (
        state: IPreviewComponentState,
        changedSettingValues: ISettingValueEx[]
    ) => {
        if (changedSettingValues.length > 0) {
            this.saveSettingValuesToCache(state, changedSettingValues);
        }

        return state;
    };

    /////////////////////////////////////////////////////////////////
    // Implementation
    /////////////////////////////////////////////////////////////////

    /**
     * Merges cached setting values when calculated setting values
     * for a target level
     * @param newSettingsValue New setting values
     * @param cachedValues Cached setting values
     * @param level Current level being merged
     * @param targetLevel Target setting level
     */
    private mergeSettingValues = (
        newSettingsValue: ISettingValueEx[],
        cachedValues: ISettingValueEx[],
        level: SettingLevel,
        targetLevel: SettingLevel
    ) => {
        newSettingsValue
            .filter(i => !i.Locked)
            .forEach(i => {
                const cachedValue = cachedValues.find(j => j.SettingName === i.SettingName);
                if (cachedValue !== undefined) {
                    i.Value = cachedValue.Value;
                    i.Locked = cachedValue.Locked;
                    i.ValueSource = {
                        Level: cachedValue.ValueSource.Level,
                        UID: cachedValue.ValueSource.UID,
                        Name: cachedValue.ValueSource.Name,
                        LockApplied: cachedValue.Locked && level !== targetLevel
                    };
                }
            });
    };

    /**
     * Saves the current values to the cache
     * @param state
     */
    private saveSettingValuesToCache = (
        state: IPreviewComponentState,
        changedSettingValues: ISettingValueEx[]
    ) => {
        const currentLevel = state.currentLevel;
        const currentValues = state.currentSettingValues;

        logger.logInformation("Saving changes", {
            currentLevel: SettingLevel[currentLevel],
            changes: JSON.stringify(changedSettingValues)
        });

        changedSettingValues.forEach(i => {
            const currentSetting = currentValues.find(
                currentItem => currentItem.SettingName === i.SettingName
            );
            if (currentSetting === undefined) {
                logger.logError("Current changed setting not found");
                throw new Error("Current changed setting not found");
            }
            currentSetting.Value = i.Value;
            currentSetting.Locked = i.Locked;
        });

        currentValues.forEach(i => {
            i.ValueSource = {
                Level: currentLevel,
                UID: SettingLevelToUID[currentLevel],
                Name: PreviewComponentCommands.GetFakeName(currentLevel),
                LockApplied: false
            };
        });

        state.cachedSettingValues[currentLevel] = currentValues;
    };

    /**
     * For the evaluation strategy and new setting level, determine
     * which setting levels need to be merged
     * @param strategy Evaluation strategy
     * @param level New setting level
     * @returns Array of setting levels that merge uses to calculate the merged setting values
     */
    private calcLevelsToMerge = (
        strategy: EvaluationStrategy,
        level: SettingLevel
    ): SettingLevel[] => {
        let result: SettingLevel[] = [level];

        switch (strategy) {
            case EvaluationStrategy.Classic:
                if (
                    SettingLevelToAccessLevel[level] <= SettingLevelToAccessLevel[SettingLevel.User]
                ) {
                    result = [
                        SettingLevel.Organization,
                        SettingLevel.Site,
                        SettingLevel.Group,
                        SettingLevel.User
                    ].filter(i => SettingLevelToAccessLevel[i] <= SettingLevelToAccessLevel[level]);
                }
                break;

            case EvaluationStrategy.OrgGroups:
                if (
                    SettingLevelToAccessLevel[level] <= SettingLevelToAccessLevel[SettingLevel.User]
                ) {
                    result = [
                        SettingLevel.Organization,
                        SettingLevel.Group,
                        SettingLevel.User
                    ].filter(i => SettingLevelToAccessLevel[i] <= SettingLevelToAccessLevel[level]);
                }
                break;

            case EvaluationStrategy.NonHierarchical:
                //nop
                break;
        }

        return result;
    };
}
